import {
  type AnalyticsPlatformContext,
  getAnalyticsPlatformContext,
} from './platformContext'

type AnalyticsJson =
  | null
  | boolean
  | number
  | string
  | AnalyticsJson[]
  | {[key: string]: AnalyticsJson}

export type AnalyticsProperties = Record<string, AnalyticsJson>

type AnalyticsAdapter = {
  track: (eventName: string, properties?: AnalyticsProperties) => void
  identify: (userId: string, traits?: AnalyticsProperties) => void
  reset: () => void
  registerGlobalProps: (properties: AnalyticsProperties) => void
}

let adapter: AnalyticsAdapter | null = null
const MAX_TRACK_QUEUE = 200
const queuedTrackEvents: Array<{
  eventName: string
  properties: AnalyticsProperties
}> = []

export function setAnalyticsAdapter(nextAdapter: AnalyticsAdapter | null) {
  adapter = nextAdapter
  if (adapter && queuedTrackEvents.length > 0) {
    const pending = queuedTrackEvents.splice(0, queuedTrackEvents.length)
    for (const event of pending) {
      try {
        adapter.track(event.eventName, event.properties)
      } catch {
        // Best effort flush only; never interrupt app flows.
      }
    }
  }
}

function withPlatformContext(
  properties: AnalyticsProperties = {},
): AnalyticsProperties & AnalyticsPlatformContext {
  return {
    ...properties,
    ...getAnalyticsPlatformContext(),
  }
}

export function trackAnalyticsEvent(
  eventName: string,
  properties: AnalyticsProperties = {},
) {
  const propertiesWithPlatform = withPlatformContext(properties)
  try {
    if (adapter) {
      adapter.track(eventName, propertiesWithPlatform)
      return
    }
    if (queuedTrackEvents.length >= MAX_TRACK_QUEUE) {
      queuedTrackEvents.shift()
    }
    queuedTrackEvents.push({eventName, properties: propertiesWithPlatform})
  } catch {
    // Analytics must never interrupt product behavior.
  }
}

export function identifyAnalyticsUser(
  userId: string,
  traits: AnalyticsProperties = {},
) {
  try {
    adapter?.identify(userId, withPlatformContext(traits))
  } catch {
    // Analytics must never interrupt product behavior.
  }
}

export function resetAnalyticsIdentity() {
  try {
    adapter?.reset()
  } catch {
    // Analytics must never interrupt product behavior.
  }
}

export function registerAnalyticsGlobalProperties(
  properties: AnalyticsProperties = {},
) {
  try {
    adapter?.registerGlobalProps(withPlatformContext(properties))
  } catch {
    // Analytics must never interrupt product behavior.
  }
}

export {getAnalyticsPlatformContext}
export {syncAnalyticsIdentity} from './identitySync'
export {getIdentityTransition} from './identityTransition'
